import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import tracking, { TrackingContentGroup } from 'src/tracking';
import Head from 'next/head';
import { jsonLdScriptProps } from 'react-schemaorg';
import { Article as ArticleDTS, ItemList } from 'schema-dts';
import { Article, ArticleBlockUnion, ArticleType, ArticleUnion, CulinaryArticle, PageTemplate } from '../../graphql/generated/api-graphql';
import ArticleTemplate from '../../templates/ArticleTemplate';
import { Icons } from '../../components/atoms/Icon';
import { HeroProps } from '../../components/molecules/Hero';
import { wediaImagesTransform } from '../Common/wedia';
import Router from '../../routes/Router';
import routes from '../../routes/definitions';
import ArticleBlock from '../../components/molecules/ArticleBlock';
import ArticleBlocks from '../ArticleBlocks';
import { transformArticleCard } from '../ArticleCard/transformArticleCard';
import { transformTagForCloud } from '../Common/tag';
import { TagsCloudProps } from '../../components/molecules/TagsCloud';
import BrandPush from '../../components/molecules/BrandPush';
import BrandPushTransform from '../BrandPush/transform';
import { CardsBlockProps } from '../../components/molecules/CardsBlock';
import ArticleQuiz from '../../components/molecules/ArticleQuiz';
import { useArticleQuiz } from '../ArticleQuiz/useArticleQuiz';
import { usePageActions } from '../Common/usePageActions';
import type { PageProps } from '../Common/PageProps';
import { app } from '../../configuration';
import ArticleRecipesBlock from '../../components/molecules/ArticleRecipesBlock';
import { resolveArticleRoute } from '../article/resolveArticleRoute';
import { buildTableOfContents, insertIdsInBlocks, insertIdsInIntro } from '../ArticleBlocks/tableOfContents';
import TableOfContents from '../../components/molecules/TableOfContents';
import { HtmlLayout } from '../../components/atoms/Html/types';
import { adPropsTransformer } from '../Common/adPropsTransformer';
import type { BreadcrumbProps } from '../../components/molecules/Breadcrumb';
import HeaderWrapper from '../Header/HeaderWrapper';
import FooterWrapper from '../Footer/FooterWrapper';
import BreadcrumbWrapper from '../Breadcrumb/BreadcrumbWrapper';
import useArticleRecipes from './useArticleRecipes';
import { jsonLdArticle, jsonLdArticleList } from './article';
export type ArticlePageProps = PageProps & {
  data: ArticleUnion;
  articles: Article[];
};
export const ArticlePage = ({
  data,
  articles,
  configuration
}: ArticlePageProps) => {
  const {
    t
  } = useTranslation();
  useEffect(() => {
    tracking.page({
      brand: undefined,
      group: TrackingContentGroup.ARTICLE,
      page: data?.title,
      section: undefined,
      hideRubrique: true
    });
  }, [data?.title]);
  const breadcrumbEntries: BreadcrumbProps['entries'] = useMemo(() => [data && 'type' in data && data?.type === ArticleType.Cheese ? {
    label: configuration?.pagesByTemplate?.[PageTemplate.HpCheesesCulture]?.title ?? t('breadcrumb_culture'),
    href: configuration?.pagesByTemplate?.[PageTemplate.HpCheesesCulture]?.slug ?? ''
  } : data && 'type' in data && data?.type === ArticleType.Health ? {
    label: configuration?.pagesByTemplate?.[PageTemplate.PositiveFood]?.title ?? t('breadcrumb_sante'),
    href: configuration?.pagesByTemplate?.[PageTemplate.PositiveFood]?.slug ?? ''
  } : {}, {
    label: data?.title
  }], [configuration?.pagesByTemplate, data, t]);
  const heroProps: HeroProps = {
    title: data?.title,
    imageProps: {
      maxWidth: 1280,
      width: 1280,
      height: 512,
      withHD: true,
      withLazyLoading: false,
      images: wediaImagesTransform(data?.mainImage),
      alt: data?.title ?? ''
    },
    mobileImageProps: {
      maxWidth: 370,
      width: 370,
      height: 200,
      withHD: true,
      withLazyLoading: false,
      images: wediaImagesTransform(data?.mainImage),
      alt: data?.title ?? ''
    },
    brandLogoProps: data?.brand?.pictureLogo ? {
      maxWidth: 1280,
      withHD: true,
      withLazyLoading: false,
      alt: data?.brand?.pictureLogo.alt ?? '',
      images: wediaImagesTransform(data.brand?.pictureLogo)
    } : undefined,
    date: data.updatedAt || data.publicationDate ? {
      text: data.updatedAt ? dayjs(data.updatedAt, 'YYYY-MM-DD HH:mm:ss').format('LL') : data.publicationDate ? dayjs(data.publicationDate, 'YYYY-MM-DD HH:mm:ss').format('LL') : '',
      time: data.updatedAt ? dayjs(data.updatedAt, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD') : data.publicationDate ? dayjs(data.publicationDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD') : ''
    } : undefined
  };
  const articlesProps: CardsBlockProps | undefined = articles && articles.length > 0 ? {
    title: t('article_recommended_articles'),
    cards: articles.map(article => transformArticleCard(t, article, t('article_recommended_articles'))),
    actionButtonProps: data && 'type' in data && data?.type === ArticleType.Cheese ? {
      label: t('more_articles'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: configuration?.pagesByTemplate?.[PageTemplate.HpCheesesCulture]?.slug ?? '',
      onClick: () => tracking.cta(t('more_articles'), t('article_recommended_articles'))
    } : data && 'type' in data && data?.type === ArticleType.Health ? {
      label: t('more_articles'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: configuration?.pagesByTemplate?.[PageTemplate.PositiveFood]?.slug ?? '',
      onClick: () => tracking.cta(t('more_articles'), t('article_recommended_articles'))
    } : undefined
  } : undefined;
  const tagsCloudProps: TagsCloudProps = {
    title: t('more_cheese'),
    tags: [...(data?.positiveFood ? [{
      label: t('positive_food'),
      href: Router.getRouteUrl(routes.tag, {
        slug: 'positivefood'
      }),
      onClick: () => tracking.tag(t('positive_food'), data?.title ?? '')
    }] : []), ...(data.tags && data.tags.length > 0 ? data.tags.map(tag => transformTagForCloud(tag, data?.title)) : [])]
  };
  const articleQuizProps = useArticleQuiz(t, data?.quiz);
  const actionsProps = usePageActions({
    id: data.id,
    text: data?.title,
    url: app.APP_URL + Router.getRouteUrl(resolveArticleRoute(data), {
      slug: data?.slug
    }),
    brand: data?.brand?.name
  });
  const adProps = adPropsTransformer(data?.brand ?? undefined, configuration, true);
  const articleRecipesProps = useArticleRecipes((data as CulinaryArticle)?.recipes);

  // Trigger or not the computation related to the table of contents
  const showTableOfContents = data?.show_table_of_contents;
  const tableOfContentsProps = showTableOfContents ? buildTableOfContents(data?.introduction || '', data?.blocks, articleRecipesProps, t) : null;
  // this will modify the introduction and blocks to add ids to the h2 and h3
  const introduction: string = showTableOfContents ? insertIdsInIntro(data?.introduction || '') : data?.introduction || '';
  const blocks: ArticleBlockUnion[] = showTableOfContents ? insertIdsInBlocks(data?.blocks) : data?.blocks || [];
  const jsonLd = jsonLdArticle(data);
  const jsonLdArticleL = articleRecipesProps ? jsonLdArticleList(data?.title ?? '', articleRecipesProps) : undefined;
  return <ArticleTemplate header={<HeaderWrapper data={configuration?.header} socials={configuration?.footer?.socialLinks} />} footer={<FooterWrapper data={configuration?.footer} />} breadcrumb={<BreadcrumbWrapper entries={breadcrumbEntries} />} heroProps={heroProps} adProps={adProps} sideContent={<>
          {data.brand && <BrandPush {...BrandPushTransform({
      t: t,
      brand: data.brand
    })} />}
        </>} intro={data?.description ? {
    text: data?.description
  } : undefined} actions={actionsProps} articlesProps={articlesProps} tagsCloudProps={tagsCloudProps} topContent={<>
          {tableOfContentsProps && <TableOfContents {...tableOfContentsProps} />}
        </>}>
      {jsonLd && <Head>
          <script {...jsonLdScriptProps<ArticleDTS>({
        '@context': 'https://schema.org',
        ...jsonLd
      })} />
        </Head>}
      {jsonLdArticleL && <Head>
          <script {...jsonLdScriptProps<ItemList>({
        '@context': 'https://schema.org',
        ...jsonLdArticleL
      })} />
        </Head>}
      {introduction && <ArticleBlock htmlProps={{
      text: introduction,
      layout: HtmlLayout.article
    }} />}
      {articleRecipesProps && <ArticleRecipesBlock {...articleRecipesProps} />}
      {blocks?.length > 0 && <ArticleBlocks blocks={blocks} />}
      {articleQuizProps && <ArticleQuiz {...articleQuizProps}></ArticleQuiz>}
    </ArticleTemplate>;
};